import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  List,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import useValuesStore from "../../../store/values-store";

const { Text } = Typography;
const asList = (data) => (Array.isArray(data) ? data : data?.results || []);
const reconciliationChecks = (row) =>
  Array.isArray(row?.checks) ? row.checks : [];
const statusColor = (status) =>
  ({
    Completed: "green",
    "Partially Completed": "orange",
    Failed: "red",
    Running: "blue",
    Queued: "default",
    Passed: "green",
    Warning: "orange",
    Open: "red",
    Acknowledged: "orange",
    Resolved: "green",
  })[status] || "default";
const idempotencyKey = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const PayrollOperationsPanel = () => {
  const navigate = useNavigate();
  const valuesStore = useValuesStore();
  const permissions = useMemo(
    () => new Set((valuesStore.permissions || []).map((item) => item.name)),
    [valuesStore.permissions],
  );
  const canView = permissions.has("finance_payroll_operations_view");
  const canManage = permissions.has("finance_payroll_operations_manage");
  const [form] = Form.useForm();
  const [jobs, setJobs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [resultJob, setResultJob] = useState(null);
  const operation = Form.useWatch("operation", form);

  const refresh = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const [jobResponse, alertResponse, reconciliationResponse, runResponse] =
        await Promise.all([
          Utilities.fetchWithAuth(
            "GET",
            domainSettings.urls.finance.payrollOperationJobs,
            null,
            navigate,
          ),
          Utilities.fetchWithAuth(
            "GET",
            domainSettings.urls.finance.payrollOperationalAlerts,
            null,
            navigate,
          ),
          Utilities.fetchWithAuth(
            "GET",
            domainSettings.urls.finance.payrollReconciliations,
            null,
            navigate,
          ),
          Utilities.fetchWithAuth(
            "GET",
            domainSettings.urls.finance.payrollRuns,
            null,
            navigate,
          ),
        ]);
      if (jobResponse.ok) setJobs(asList(jobResponse.data));
      if (alertResponse.ok) setAlerts(asList(alertResponse.data));
      if (reconciliationResponse.ok)
        setReconciliations(asList(reconciliationResponse.data));
      if (runResponse.ok) setRuns(asList(runResponse.data));
    } finally {
      setLoading(false);
    }
  }, [canView, navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    if (!jobs.some((job) => ["Queued", "Running"].includes(job.status)))
      return undefined;
    const timer = window.setInterval(refresh, 3000);
    return () => window.clearInterval(timer);
  }, [jobs, refresh]);

  if (!canView) return null;

  const queueOperation = async () => {
    const values = await form.validateFields();
    setQueueing(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "POST",
        domainSettings.urls.finance.payrollOperationJobs,
        {
          operation: values.operation,
          payrollRunId: values.payrollRunId,
          parameters:
            values.operation === "Final Schedule Export"
              ? { format: values.format || "xlsx" }
              : values.operation === "Bulk Payslip Email"
                ? { retryFailed: false }
                : values.operation === "Reconciliation"
                  ? { stage: "Manual" }
                  : {},
          idempotencyKey: idempotencyKey(),
        },
        navigate,
      );
      if (!response.ok)
        return Utilities.showError(
          response,
          "Could not queue the payroll operation",
        );
      message.success(
        "Payroll operation queued. You can safely leave this page.",
      );
      form.resetFields();
      refresh();
    } finally {
      setQueueing(false);
    }
  };

  const retry = async (job) => {
    const url = domainSettings.urls.finance.payrollOperationJobRetry.replace(
      ":id",
      job.jobId,
    );
    const response = await Utilities.fetchWithAuth("POST", url, {}, navigate);
    if (!response.ok)
      return Utilities.showError(
        response,
        "Could not retry the payroll operation",
      );
    message.success(
      "A safe retry was queued. Completed work will not be duplicated.",
    );
    refresh();
  };

  const clearFinishedJobs = async () => {
    if (
      !jobs.some((job) =>
        ["Completed", "Partially Completed", "Failed"].includes(job.status),
      )
    ) {
      return message.info("There are no finished operation jobs to clear");
    }
    const response = await Utilities.fetchWithAuth(
      "POST",
      domainSettings.urls.finance.payrollOperationJobsClear,
      { allFinished: true },
      navigate,
    );
    if (!response.ok)
      return Utilities.showError(response, "Could not clear operation jobs");
    message.success(
      `${response.data.cleared} finished operation job(s) cleared`,
    );
    refresh();
  };

  const download = async (job) => {
    const endpoint =
      domainSettings.urls.finance.payrollOperationJobDownload.replace(
        ":id",
        job.jobId,
      );
    const response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${Utilities.getCookie("access")}` },
    });
    if (!response.ok) return message.error("The operation file is not ready");
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    const disposition = response.headers.get("content-disposition") || "";
    link.download =
      disposition.match(/filename="?([^";]+)"?/i)?.[1] ||
      `${job.operation.replaceAll(" ", "_")}_${job.periodName || job.jobId}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const changeAlert = async (alert, action) => {
    const endpoint = domainSettings.urls.finance.payrollOperationalAlertAction
      .replace(":id", alert.alertId)
      .replace(":action", action);
    const response = await Utilities.fetchWithAuth(
      "POST",
      endpoint,
      {},
      navigate,
    );
    if (!response.ok)
      return Utilities.showError(response, "Could not update the alert");
    refresh();
  };

  const openAlerts = alerts.filter((item) => item.status !== "Resolved");
  const latestByRun = Object.values(
    reconciliations.reduce((result, item) => {
      if (!result[item.payrollRunId]) result[item.payrollRunId] = item;
      return result;
    }, {}),
  );

  return (
    <Card
      title={
        <Space>
          <SafetyCertificateOutlined /> Payroll operations and integrity
        </Space>
      }
      extra={
        <Space>
          {canManage && (
            <Popconfirm
              title="Clear all finished operation jobs?"
              description="Generated files and finished job entries will be cleared. Running and queued jobs, alerts, and reconciliation evidence will remain."
              onConfirm={clearFinishedJobs}
            >
              <Button danger size="small" icon={<DeleteOutlined />}>
                Clear finished
              </Button>
            </Popconfirm>
          )}
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={refresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      }
    >
      <Alert
        type={
          openAlerts.some((item) => item.severity === "Critical")
            ? "error"
            : "info"
        }
        showIcon
        message={
          openAlerts.length
            ? `${openAlerts.length} payroll operational alert(s) need attention.`
            : "No unresolved payroll operational alerts."
        }
        style={{ marginBottom: 16 }}
      />
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Statistic
            title="Queued"
            value={jobs.filter((item) => item.status === "Queued").length}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Running"
            value={jobs.filter((item) => item.status === "Running").length}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Completed"
            value={jobs.filter((item) => item.status === "Completed").length}
            valueStyle={{ color: "#228B22" }}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Needs attention"
            value={
              jobs.filter((item) =>
                ["Failed", "Partially Completed"].includes(item.status),
              ).length + openAlerts.length
            }
            valueStyle={{ color: "#cf1322" }}
          />
        </Col>
      </Row>

      {canManage && (
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item
            name="payrollRunId"
            rules={[{ required: true, message: "Select a payroll run" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Payroll run"
              style={{ minWidth: 240 }}
              options={runs.map((run) => ({
                value: run.runId,
                label: `${run.periodName} · ${run.status}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="operation"
            rules={[{ required: true, message: "Select an operation" }]}
          >
            <Select
              placeholder="Background operation"
              style={{ minWidth: 220 }}
              options={[
                "Payslip ZIP",
                "Combined Payslip PDF",
                "Final Schedule Export",
                "Bulk Payslip Email",
                "Reconciliation",
              ].map((value) => ({ value, label: value }))}
            />
          </Form.Item>
          {operation === "Final Schedule Export" && (
            <Form.Item name="format" initialValue="xlsx">
              <Select
                style={{ width: 110 }}
                options={["xlsx", "csv", "pdf"].map((value) => ({
                  value,
                  label: value.toUpperCase(),
                }))}
              />
            </Form.Item>
          )}
          <Form.Item>
            <Button type="primary" loading={queueing} onClick={queueOperation}>
              Queue
            </Button>
          </Form.Item>
        </Form>
      )}

      <Table
        rowKey="jobId"
        size="small"
        loading={loading}
        dataSource={jobs.slice(0, 20)}
        pagination={false}
        scroll={{ x: 1050 }}
        columns={[
          { title: "Operation", dataIndex: "operation" },
          {
            title: "Payroll",
            dataIndex: "periodName",
            render: (value) => value || "—",
          },
          {
            title: "Status",
            dataIndex: "status",
            render: (value) => <Tag color={statusColor(value)}>{value}</Tag>,
          },
          {
            title: "Progress",
            width: 180,
            render: (_, row) => (
              <Progress
                percent={row.progress}
                size="small"
                status={row.status === "Failed" ? "exception" : undefined}
              />
            ),
          },
          {
            title: "Items",
            render: (_, row) =>
              `${row.completedItems}/${row.totalItems}${row.failedItems ? ` · ${row.failedItems} failed` : ""}${row.skippedItems ? ` · ${row.skippedItems} skipped` : ""}`,
          },
          { title: "Engine", dataIndex: "engineVersion" },
          {
            title: "Started",
            dataIndex: "createdAt",
            render: (value) => dayjs(value).format("DD MMM HH:mm"),
          },
          {
            title: "Message",
            dataIndex: "safeErrorMessage",
            render: (value) => value || "—",
          },
          {
            title: "",
            fixed: "right",
            render: (_, row) => (
              <Space>
                {row.hasFile && (
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => download(row)}
                  >
                    Download
                  </Button>
                )}
                {row.operation === "Profile Import" &&
                  (row.status === "Completed" ||
                    row.status === "Failed" ||
                    row.failedItems > 0) && (
                    <Button size="small" onClick={() => setResultJob(row)}>
                      {row.failedItems > 0 ? "View errors" : "View summary"}
                    </Button>
                  )}
                {canManage &&
                  row.operation !== "Profile Import" &&
                  ["Failed", "Partially Completed"].includes(row.status) && (
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => retry(row)}
                    >
                      Retry
                    </Button>
                  )}
              </Space>
            ),
          },
        ]}
      />

      {openAlerts.length > 0 && (
        <Card title="Operational alerts" size="small" style={{ marginTop: 16 }}>
          <Table
            rowKey="alertId"
            size="small"
            pagination={false}
            dataSource={openAlerts}
            columns={[
              {
                title: "Severity",
                dataIndex: "severity",
                render: (value) => (
                  <Tag color={value === "Critical" ? "red" : "orange"}>
                    {value}
                  </Tag>
                ),
              },
              {
                title: "Payroll",
                dataIndex: "periodName",
                render: (value) => value || "System job",
              },
              { title: "Safe message", dataIndex: "safeMessage" },
              {
                title: "Status",
                dataIndex: "status",
                render: (value) => (
                  <Tag color={statusColor(value)}>{value}</Tag>
                ),
              },
              {
                title: "",
                render: (_, row) =>
                  canManage && (
                    <Space>
                      {row.status === "Open" && (
                        <Button
                          size="small"
                          onClick={() => changeAlert(row, "acknowledge")}
                        >
                          Acknowledge
                        </Button>
                      )}
                      <Button
                        size="small"
                        icon={<CheckCircleOutlined />}
                        onClick={() => changeAlert(row, "resolve")}
                      >
                        Resolve
                      </Button>
                    </Space>
                  ),
              },
            ]}
          />
        </Card>
      )}

      {latestByRun.length > 0 && (
        <Card
          title="Latest reconciliation by payroll"
          size="small"
          style={{ marginTop: 16 }}
        >
          <Table
            rowKey="reconciliationId"
            size="small"
            pagination={false}
            dataSource={latestByRun}
            columns={[
              { title: "Payroll", dataIndex: "periodName" },
              { title: "Stage", dataIndex: "stage" },
              {
                title: "Result",
                dataIndex: "status",
                render: (value) => (
                  <Tag color={statusColor(value)}>{value}</Tag>
                ),
              },
              {
                title: "Calculation",
                render: (_, row) =>
                  `v${row.calculationVersion} · engine ${row.engineVersion}`,
              },
              {
                title: "Checks",
                render: (_, row) => {
                  const checks = reconciliationChecks(row);
                  return `${checks.filter((item) => item.status === "Passed").length}/${checks.length} passed`;
                },
              },
              {
                title: "Checked",
                dataIndex: "createdAt",
                render: (value) => dayjs(value).format("DD MMM YYYY HH:mm"),
              },
            ]}
          />
        </Card>
      )}
      <Text type="secondary">
        Only safe summaries are shown here. Salary, tax, bank and member details
        remain protected by their existing permissions.
      </Text>

      <Modal
        open={!!resultJob}
        onCancel={() => setResultJob(null)}
        footer={null}
        title={
          resultJob?.failedItems > 0
            ? "Payroll profile import errors"
            : "Payroll profile import summary"
        }
      >
        {resultJob && (
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            {(resultJob.resultSummary || {}).message && (
              <Text type="success">
                {(resultJob.resultSummary || {}).message}
              </Text>
            )}
            {resultJob.failedItems > 0 && (
              <Text type="secondary">
                {`${resultJob.failedItems || 0} row(s) need attention, ` +
                  `${resultJob.skippedItems || 0} skipped. No profiles were saved.`}
              </Text>
            )}
            {!resultJob.failedItems && resultJob.skippedItems > 0 && (
              <Text type="secondary">
                {`${resultJob.skippedItems} row(s) skipped.`}
              </Text>
            )}
            <List
              size="small"
              bordered
              dataSource={[
                ...((resultJob.resultSummary || {}).errors || []),
                ...((resultJob.resultSummary || {}).skipped_details || []).map(
                  (item) => `Row ${item.row}: ${item.reason} (skipped)`,
                ),
              ]}
              renderItem={(item) => <List.Item>{item}</List.Item>}
              locale={{ emptyText: "No errors reported" }}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
};

export default PayrollOperationsPanel;
